import React from 'react';
import { accentUtils } from '../../services/HelperService';
import { AccentPromptComposer } from './AccentPromptComposer';

const safeString = (v) => (v ?? '').toString();

const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

/**
 * Prompt composer that appends user messages to a controlled messages array.
 *
 * Props:
 * - messages: the current messages array (controlled)
 * - onMessagesChange(nextMessages): called when a user sends a prompt
 * - onSendPrompt({ text, history, intent }): async; history is role-aware
 *   oldest->newest; intent is 'ask' | 'change' when an explicit intent button was
 *   pressed (see AccentPromptComposer), undefined otherwise
 * - askLabel? / sendLabel?: enable and label the explicit intent buttons
 * - disabled?
 * - placeholder?
 *
 * Ref (imperative):
 * - send(text, intent): sends the given text exactly as if the user clicked Send —
 *   the message lands in the transcript (with intent label), onSendPrompt runs, and
 *   the Send button shows its built-in click spinner for the duration. Hosts that
 *   auto-start a conversation (e.g. profiles with autoFirstPrompt) should use this
 *   instead of calling onSendPrompt themselves.
 */
export const AccentPromptChat = React.forwardRef(({
  messages,
  onMessagesChange,
  onSendPrompt,
  disabled,
  promptLabel,
  placeholder,
  templateCatalog,
  readonly,
  askLabel,
  sendLabel,
}, ref) => {
  const list = React.useMemo(() => (Array.isArray(messages) ? messages : []), [messages]);
  const [value, setValue] = React.useState('');

  const composerRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    send: (text, intent) => composerRef.current?.send(text, intent),
  }));

  const doSend = React.useCallback(async (text, intent) => {
    const trimmed = safeString(text).trim();
    if (accentUtils.isEmpty(trimmed)) return false;

    // Intent rides the stored message too, so later turns' server transcripts keep
    // which messages were explicit questions vs change requests. intentLabel is the
    // clicked button's text (client-side display only): the history renders it above
    // the bubble so the user can see which action each message was sent with.
    const intentLabel = intent === 'ask' ? safeString(askLabel) : (intent === 'change' ? safeString(sendLabel) : '');
    const msg = { id: makeId(), role: 'user', text: trimmed, ts: new Date().toISOString(), ...(intent ? { intent, intentLabel } : {}) };
    // Never capped: trimming here once let system-message noise evict the real
    // conversation — including from the history handed to the server, so the AI lost
    // its context too. Transcript size is managed server-side at prompt-build time.
    const nextMessages = [...list, msg];
    onMessagesChange?.(nextMessages);

    const history = nextMessages.map(m => ({
      role: safeString(m?.role || 'user'),
      text: safeString(m?.text),
      intent: safeString(m?.intent),
    })).filter(m => !accentUtils.isEmpty(m.text));

    const res = await onSendPrompt?.({ text: trimmed, history, intent });
    if (typeof res === 'boolean') return res;
    const ok = !!(res?.TestOk ?? res?.testOk ?? res?.ok ?? res?.success);
    if (typeof res === 'undefined') return true;
    return ok;
  }, [askLabel, list, onMessagesChange, onSendPrompt, sendLabel]);

  return (
    <AccentPromptComposer
      ref={composerRef}
      disabled={!!disabled || !!readonly}
      label={promptLabel}
      value={value}
      onChange={setValue}
      placeholder={placeholder}
      templateCatalog={templateCatalog}
      askLabel={askLabel}
      sendLabel={sendLabel}
      onSend={async ({ text, intent }) => {
        const ok = await doSend(text, intent);
        if (ok) setValue('');
      }}
    />
  );
});
